import React, { useEffect, useMemo, useState, useCallback } from "react"; // Added useCallback
import { Card, CardContent, Typography, Box, Autocomplete, MenuItem, Table, TableHead, TableRow, TableCell, TableBody, TableContainer, Paper, Grid, Chip, IconButton, Tooltip, Divider, CircularProgress, Button, TextField, } from "@mui/material";
import { Download as DownloadIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import * as XLSX from "xlsx";
import { getData } from "../../../services/axiosInstance";
import { API_ENDPOINTS } from "../../../services/endpoints";
import PayslipPreview from "./payslipPreview";

const MONTHS = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

function PayslipDownloadTab() {
    const [fromMonth, setFromMonth] = useState(MONTHS[new Date().getMonth() - 1]);
    const [fromYear, setFromYear] = useState(new Date().getFullYear());
    const [toMonth, setToMonth] = useState(MONTHS[new Date().getMonth()]);
    const [toYear, setToYear] = useState(new Date().getFullYear());
    const [departmentId, setDepartmentId] = useState("");
    const [employee, setEmployee] = useState(null);
    const [isEmployeeSelected, setIsEmployeeSelected] = useState(false);
    const [departments, setDepartments] = useState([]);
    const [allEmployees, setAllEmployees] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [rows, setRows] = useState([]);
    const [rawResponse, setRawResponse] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    /* ---------------- YEARS ---------------- */
    const years = useMemo(() => {
        const list = [];
        for (let y = new Date().getFullYear() - 5; y <= new Date().getFullYear() + 1; y++) {
            list.push(y);
        }
        return list;
    }, []);

    const toYears = useMemo(() => {
        if (!fromYear) return years;
        return years.filter((y) => y >= fromYear);
    }, [fromYear, years]);

    // useEffect(() => {
    //     setToYear("");
    // }, [fromYear]);

    /* ---------------- FETCH FUNCTIONS (Stable with useCallback) ---------------- */

    const loadDepartments = useCallback(async () => {
        const res = await getData(API_ENDPOINTS.getAllDepartments);
        if (res?.statusCode === 200) {
            setDepartments(res.message.departments || []);
        }
    }, []);

    const loadEmployees = useCallback(async () => {

        const res = await getData(
            API_ENDPOINTS.getEmployeePayslipByDateRange("Jan", years[0], "Dec", years.at(-1)),
            {}
        );

        if (res?.statusCode === 200 && Array.isArray(res.result?.employees)) {
            const map = new Map();
            res.result.employees.forEach((emp) => {
                emp.payslips?.forEach((p) => {
                    const pid = p?.earnings?.employeePersonalId;
                    if (pid && !map.has(pid)) {
                        map.set(pid, {
                            employeeId: emp.employeeId,
                            employeePersonalId: pid,
                            employeeName: emp.employeeName,
                            department: emp.department,
                        });
                    }
                });
            });
            const list = Array.from(map.values());
            setAllEmployees(list);
            setEmployees(list);
        }
    }, [years]);

    const fetchPayslips = useCallback(async () => {
        setLoading(true);
        setRows([]);

        const res = await getData(API_ENDPOINTS.getEmployeePayslipByDateRange(
            fromMonth, fromYear, toMonth, toYear, employee?.employeePersonalId, departmentId));

        if (res?.statusCode === 200 && Array.isArray(res.result?.employees)) {
            setRawResponse(res.result);
            const flat = [];
            res.result.employees.forEach((emp) => {
                emp.payslips?.forEach((p) => {
                    flat.push({
                        employeeId: emp.employeeId,
                        employeePersonalId: p.earnings?.employeePersonalId,
                        employeeName: emp.employeeName,
                        department: emp.department,
                        month: p.month,
                        year: p.year,
                        netSalary: p.netSalary,
                        isPayslipGenerated: p.isPayslipGenerated,
                    });
                });
            });
            setRows(flat);
        }
        setLoading(false);
    }, [fromMonth, fromYear, toMonth, toYear, departmentId, isEmployeeSelected, employee]);

    /* ---------------- EFFECTS ---------------- */

    // Line 53 Fix: Added loadEmployees and loadDepartments to dependencies
    useEffect(() => {
        loadDepartments();
        loadEmployees();
    }, [loadDepartments, loadEmployees]);

    /* ---------------- FILTER EMPLOYEES BY DEPARTMENT ---------------- */
    useEffect(() => {
        if (!departmentId) {
            setEmployees(allEmployees);
        } else {
            const deptName = departments.find(
                (d) => String(d.id) === String(departmentId)
            )?.department;

            setEmployees(allEmployees.filter((e) => e.department === deptName));
        }
        setEmployee(null);
        setIsEmployeeSelected(false);
    }, [departmentId, allEmployees, departments]);

    /* ---------------- AUTO FETCH ---------------- */
    // Line 115 Fix: Included fetchPayslips in dependencies
    useEffect(() => {
        if (!fromMonth || !fromYear || !toMonth || !toYear) return;
        fetchPayslips();
    }, [fromMonth, fromYear, toMonth, toYear, fetchPayslips]);

    /* ---------------- EXCEL DOWNLOAD ---------------- */
    const downloadExcel = () => {
        if (!rawResponse?.employees?.length) return;

        const excelRows = [];
        rawResponse.employees.forEach((emp) => {
            emp.payslips?.forEach((p) => {
                excelRows.push({
                    "Employee ID": emp.employeeId,
                    "Employee Name": emp.employeeName,
                    "Department": emp.department,
                    "Designation": emp.designation || "",
                    "Date of Joining": emp.dateOfJoining ? new Date(emp.dateOfJoining).toLocaleDateString("en-IN") : "",
                    "Month": p.month,
                    "Year": p.year,
                    "Payslip Generated": p.isPayslipGenerated ? "Yes" : "No",
                    "Basic": Number(p.earnings?.basic || 0),
                    "HRA": Number(p.earnings?.hra || 0),
                    "Special Allowance": Number(p.earnings?.specialAllowance || 0),
                    "Conveyance Allowance": Number(p.earnings?.conveyanceAllowance || 0),
                    "Other Allowance": Number(p.earnings?.otherAllowance || 0),
                    "Gross Monthly": Number(p.earnings?.grossMonthly || 0),
                    "PF": Number(p.deductions?.pfEmployee || 0),
                    "ESI": Number(p.deductions?.esiEmployee || 0),
                    "Professional Tax": Number(p.deductions?.professionalTax || 0),
                    "TDS": Number(p.deductions?.tds || 0),
                    "Total Deductions": Number(p.deductions?.totalDeductions || 0),
                    "Working Days": Number(p.leaves?.workingDays || 0),
                    "LOP Days": Number(p.leaves?.lopCount || 0),
                    "LOP Amount": Number(p.leaves?.lopAmount || 0),
                    "Net Salary": Number(p.netSalary || 0),
                    "YTD Net Salary": Number(p.ytd?.netSalary || 0),
                });
            });
        });

        const ws = XLSX.utils.json_to_sheet(excelRows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Payslips");
        XLSX.writeFile(wb, `Payslips_${fromMonth}_${fromYear}_to_${toMonth}_${toYear}.xlsx`);
    };

    if (showPreview && previewData) {
        return (
            <PayslipPreview
                data={previewData}
                onBack={() => setShowPreview(false)}
            />
        );
    }

    return (
        <Card>
            <CardContent>
                <Typography variant="h5" fontWeight={600}>
                    Employee Payslips
                </Typography>

                <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            select fullWidth label="From Month" value={fromMonth}
                            onChange={(e) => setFromMonth(e.target.value)}
                            size="small"
                        >
                            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            select fullWidth label="From Year" value={fromYear}
                            onChange={(e) => {
                                setFromYear(e.target.value);
                                setToYear("")
                            }}
                            size="small"
                        >
                            {years.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            select fullWidth label="To Month" value={toMonth}
                            onChange={(e) => setToMonth(e.target.value)}
                            size="small"
                        >
                            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            select fullWidth label="To Year" value={toYear}
                            onChange={(e) => setToYear(e.target.value)}
                            size="small"
                        >
                            {toYears.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <TextField
                            select fullWidth label="Department" value={departmentId}
                            onChange={(e) => setDepartmentId(e.target.value)}
                            size="small"
                        >
                            <MenuItem value="">All Departments</MenuItem>
                            {departments.map((d) => (
                                <MenuItem key={d.id} value={d.id}>
                                    {d.department} ({allEmployees.filter((e) => e.department === d.department).length})
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Autocomplete
                            options={employees}
                            value={employee}
                            onChange={(e, v) => {
                                setEmployee(v);
                                setIsEmployeeSelected(!!v);
                            }}
                            getOptionLabel={(o) => o ? `${o.employeeId} - ${o.employeeName}` : ""}
                            renderInput={(params) => <TextField {...params} label="Employee" size="small" />}
                        />
                    </Grid>
                    {!isEmployeeSelected && (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <Button
                                fullWidth variant="outlined" startIcon={<DownloadIcon />}
                                disabled={!rows.length} onClick={downloadExcel}
                            >
                                Download Excel
                            </Button>
                        </Grid>
                    )}
                </Grid>

                <Divider sx={{ my: 3 }} />

                {loading ? (
                    <Box display="flex" justifyContent="center">
                        <CircularProgress />
                    </Box>
                ) : (
                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell>Employee</TableCell>
                                    <TableCell>Department</TableCell>
                                    <TableCell>Net Salary</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="center">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center">No Data</TableCell>
                                    </TableRow>
                                ) : (
                                    rows.map((r, i) => (
                                        <TableRow key={i}>
                                            <TableCell>{r.month} {r.year}</TableCell>
                                            <TableCell>{r.employeeName}</TableCell>
                                            <TableCell>{r.department}</TableCell>
                                            <TableCell>₹{r.netSalary}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={r.isPayslipGenerated ? "Generated" : "Pending"}
                                                    color={r.isPayslipGenerated ? "success" : "warning"}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Tooltip title="View Payslip">
                                                    <IconButton
                                                    disabled={!r.isPayslipGenerated}
                                                        onClick={() => {
                                                            setPreviewData(r);
                                                            setShowPreview(true);
                                                        }}
                                                    >
                                                        <VisibilityIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </CardContent>
        </Card>
    );
}

export default PayslipDownloadTab;